const DOCUMENT_LANGUAGES = new Set(["en", "sq", "de"]);
const REGIONAL_FORMATS = new Set(["XK", "GB", "US"]);
const DATE_STYLES = new Set(["iso", "medium", "numeric", "us"]);
const NUMBER_STYLES = new Set(["symbol-prefix", "symbol-suffix"]);

export const DEFAULT_DOCUMENT_PREFERENCES = Object.freeze({
  language: "en",
  region: "XK",
  dateStyle: "medium",
  numberStyle: "symbol-prefix",
});

export const parseCapturedDocumentPreferences = (value) => {
  // Older servers rendered English/ISO; do not substitute today's organization defaults.
  if (value === undefined) return { language: 'en', region: 'XK', dateStyle: 'iso', numberStyle: 'symbol-prefix' };
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).sort().join(',') !== 'dateStyle,language,numberStyle,region'
    || !DOCUMENT_LANGUAGES.has(value.language) || !REGIONAL_FORMATS.has(value.region)
    || !DATE_STYLES.has(value.dateStyle) || !NUMBER_STYLES.has(value.numberStyle)) throw new Error('The invoice document formatting is invalid. Reload the invoice.');
  return { ...value };
};

const LABELS = {
  en: {
    invoice: "INVOICE",
    creditNote: "CREDIT NOTE",
    from: "From",
    billTo: "Bill to",
    issued: "Issued",
    due: "Due",
    reference: "Reference",
    description: "Description",
    quantity: "Qty",
    rate: "Rate",
    amount: "Amount",
    subtotal: "Subtotal",
    taxableSubtotal: "Taxable subtotal",
    included: "included",
    withheld: "withheld",
    adjustment: "Adjustment",
    total: "Total",
    creditTotal: "Credit total",
    savedBillingProfile: "Saved billing profile",
    clientAddress: "Client address on file",
    notSet: "Not set",
    paymentByBank: "Payment by bank transfer.",
    thankYou: "Thank you for the partnership.",
    amountDue: "Amount due",
    kindRegards: "Kind regards",
  },
  sq: {
    invoice: "FATURË",
    creditNote: "NOTË KREDITORE",
    from: "Nga",
    billTo: "Faturuar për",
    issued: "Lëshuar",
    due: "Afati",
    reference: "Referenca",
    description: "Përshkrimi",
    quantity: "Sasia",
    rate: "Çmimi",
    amount: "Shuma",
    subtotal: "Nëntotali",
    taxableSubtotal: "Nëntotali i tatueshëm",
    included: "përfshirë",
    withheld: "mbajtur",
    adjustment: "Rregullim",
    total: "Totali",
    creditTotal: "Totali i kreditit",
    savedBillingProfile: "Profili i ruajtur i faturimit",
    clientAddress: "Adresa e klientit në dosje",
    notSet: "Nuk është caktuar",
    paymentByBank: "Pagesa përmes transferit bankar.",
    thankYou: "Faleminderit për bashkëpunimin.",
    amountDue: "Shuma për pagesë",
    kindRegards: "Me respekt",
  },
  de: {
    invoice: "RECHNUNG",
    creditNote: "GUTSCHRIFT",
    from: "Von",
    billTo: "Rechnung an",
    issued: "Ausgestellt",
    due: "Fällig",
    reference: "Referenz",
    description: "Beschreibung",
    quantity: "Menge",
    rate: "Preis",
    amount: "Betrag",
    subtotal: "Zwischensumme",
    taxableSubtotal: "Steuerpflichtige Zwischensumme",
    included: "enthalten",
    withheld: "einbehalten",
    adjustment: "Anpassung",
    total: "Gesamt",
    creditTotal: "Gutschrift gesamt",
    savedBillingProfile: "Gespeichertes Rechnungsprofil",
    clientAddress: "Kundenadresse hinterlegt",
    notSet: "Nicht festgelegt",
    paymentByBank: "Zahlung per Banküberweisung.",
    thankYou: "Vielen Dank für die Zusammenarbeit.",
    amountDue: "Fälliger Betrag",
    kindRegards: "Freundliche Grüße",
  },
};

const supported = (set, value, fallback) => set.has(value) ? value : fallback;

export const normalizeDocumentPreferences = (source = {}) => ({
  language: supported(DOCUMENT_LANGUAGES, source.documentLanguage || source.language, DEFAULT_DOCUMENT_PREFERENCES.language),
  region: supported(REGIONAL_FORMATS, source.regionalFormat || source.region, DEFAULT_DOCUMENT_PREFERENCES.region),
  dateStyle: supported(DATE_STYLES, source.documentDateStyle || source.dateStyle, DEFAULT_DOCUMENT_PREFERENCES.dateStyle),
  numberStyle: supported(NUMBER_STYLES, source.documentNumberStyle || source.numberStyle, DEFAULT_DOCUMENT_PREFERENCES.numberStyle),
});

export const documentLabels = (source) => LABELS[normalizeDocumentPreferences(source).language] || LABELS.en;

const parseDocumentDate = (value) => {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatLocalizedDocumentDate = (value, source, fallback = "") => {
  const date = parseDocumentDate(value);
  if (!date) return fallback || String(value || "");
  const preferences = normalizeDocumentPreferences(source);
  if (preferences.dateStyle === "iso") return String(value).slice(0, 10);
  if (preferences.dateStyle === "numeric") {
    return [String(date.getDate()).padStart(2, "0"), String(date.getMonth() + 1).padStart(2, "0"), date.getFullYear()].join("/");
  }
  if (preferences.dateStyle === "us") {
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
  }
  const locale = { en: preferences.region === 'US' ? 'en-US' : 'en-GB', sq: "sq-AL", de: "de-DE" }[preferences.language] || "en-GB";
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(date);
};

export const formatLocalizedDocumentCurrency = (amount, currency = "EUR", source = {}) => {
  const preferences = normalizeDocumentPreferences(source);
  const value = Number(amount || 0);
  const locale = preferences.numberStyle === "symbol-suffix" ? "de-DE" : "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0);
  } catch {
    return `${currency} ${(Number.isFinite(value) ? value : 0).toFixed(2)}`;
  }
};
